/**
 * ScrollTrigger reveals: utility classes (.gsap-fade-in, …),
 * stagger containers (.gsap-stagger*), optional [data-gsap-animate], and SplitText highlights.
 */

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, SplitText, ScrollToPlugin);

const ST = {
  start: "top 85%",
  toggleActions: "play none none none",
};

/** Skip for class-based reveals (.gsap-* on page) */
function skipReveal(el) {
  return (
    el.closest(".header-full-screen") || el.hasAttribute("data-no-animate")
  );
}

/** `[data-gsap-animate]` also skips project hero (block handles its own motion) */
function skipDataAttrGsap(el) {
  return skipReveal(el) || el.closest(".projects-hero");
}

/** Delays from .gsap-delay-* utility classes (seconds) */
function delayFromClasses(el) {
  const pairs = [
    ["gsap-delay-0", 0],
    ["gsap-delay-100", 0.1],
    ["gsap-delay-200", 0.2],
    ["gsap-delay-300", 0.3],
    ["gsap-delay-400", 0.4],
    ["gsap-delay-500", 0.5],
    ["gsap-delay-600", 0.6],
    ["gsap-delay-700", 0.7],
    ["gsap-delay-800", 0.8],
    ["gsap-delay-900", 0.9],
    ["gsap-delay-1000", 1],
    ["gsap-delay-1200", 1.2],
    ["gsap-delay-1500", 1.5],
    ["gsap-delay-2000", 2],
    ["gsap-delay-2500", 2.5],
    ["gsap-delay-3000", 3],
  ];
  for (const [cls, sec] of pairs) {
    if (el.classList.contains(cls)) return sec;
  }
  return 0;
}

const DATA_ANIMATION_PRESETS = {
  "fade-in": [
    { opacity: 0 },
    { opacity: 1, duration: 0.85, ease: "power2.out" },
  ],
  "slide-left": [
    { x: -56, opacity: 0 },
    { x: 0, opacity: 1, duration: 0.8, ease: "power2.out" },
  ],
  "slide-right": [
    { x: 56, opacity: 0 },
    { x: 0, opacity: 1, duration: 0.8, ease: "power2.out" },
  ],
  "slide-up": [
    { y: 48, opacity: 0 },
    { y: 0, opacity: 1, duration: 0.8, ease: "power2.out" },
  ],
  "slide-down": [
    { y: -48, opacity: 0 },
    { y: 0, opacity: 1, duration: 0.8, ease: "power2.out" },
  ],
  scale: [
    { scale: 0.92, opacity: 0 },
    { scale: 1, opacity: 1, duration: 0.85, ease: "back.out(1.5)" },
  ],
  rotate: [
    { rotation: -12, opacity: 0 },
    { rotation: 0, opacity: 1, duration: 0.8, ease: "power2.out" },
  ],
};

/** Class selectors → reveal presets (fade / slide / scale only) */
const CLASS_REVEALS = [
  [
    ".gsap-fade-in",
    { opacity: 0, y: -32 },
    { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" },
  ],
  [
    ".gsap-slide-left",
    { x: -56, opacity: 0 },
    { x: 0, opacity: 1, duration: 0.8, ease: "power2.out" },
  ],
  [
    ".gsap-slide-right",
    { x: 56, opacity: 0 },
    { x: 0, opacity: 1, duration: 0.8, ease: "power2.out" },
  ],
  [
    ".gsap-slide-up",
    { y: 48, opacity: 0 },
    { y: 0, opacity: 1, duration: 0.8, ease: "power2.out" },
  ],
  [
    ".gsap-slide-down",
    { y: -48, opacity: 0 },
    { y: 0, opacity: 1, duration: 0.8, ease: "power2.out" },
  ],
  [
    ".gsap-scale",
    { scale: 0.92, opacity: 0 },
    { scale: 1, opacity: 1, duration: 0.85, ease: "back.out(1.5)" },
  ],
];

class GSAPAnimationSystem {
  constructor() {
    this.animations = new Map();
    // Always called from DOMContentLoaded + microtask in main.js
    this.setupAnimations();
  }

  _singleReveal(el, from, to) {
    const delay = delayFromClasses(el);
    const tween = gsap.fromTo(el, from, {
      ...to,
      delay,
      onComplete: () => el.classList.add("gsap-animated"),
      scrollTrigger: {
        trigger: el,
        start: ST.start,
        toggleActions: ST.toggleActions,
      },
    });
    this.animations.set(el, tween);
  }

  _revealsFromClasses() {
    for (const [selector, from, to] of CLASS_REVEALS) {
      document.querySelectorAll(selector).forEach((el) => {
        if (!skipReveal(el)) this._singleReveal(el, from, to);
      });
    }
  }

  _staggerContainer(container, duration, stagger, delay = 0) {
    const children = container.children;
    if (!children.length) return;

    const tween = gsap.fromTo(
      children,
      { opacity: 0, y: -40 },
      {
        opacity: 1,
        y: 0,
        duration,
        stagger,
        delay,
        ease: "power2.out",
        onComplete: () => container.classList.add("gsap-animated"),
        scrollTrigger: {
          trigger: container,
          start: ST.start,
          toggleActions: ST.toggleActions,
        },
      },
    );
    this.animations.set(container, tween);
  }

  _dataAttributeAnimations() {
    document.querySelectorAll("[data-gsap-animate]").forEach((el) => {
      if (skipDataAttrGsap(el)) return;

      const type = el.dataset.gsapAnimate;
      const delay =
        (parseInt(el.dataset.gsapDelay || "0", 10) || 0) / 1000;

      if (type === "stagger") {
        const children = el.children;
        if (children.length) {
          const tw = gsap.fromTo(
            children,
            { opacity: 0, y: -40 },
            {
              opacity: 1,
              y: 0,
              duration: 0.75,
              delay,
              stagger: 0.08,
              ease: "power2.out",
              onComplete: () => el.classList.add("gsap-animated"),
              scrollTrigger: {
                trigger: el,
                start: ST.start,
                toggleActions: ST.toggleActions,
              },
            },
          );
          this.animations.set(el, tw);
        }
        return;
      }

      const preset = DATA_ANIMATION_PRESETS[type];
      if (!preset) return;

      const [from, to] = preset;
      const tw = gsap.fromTo(el, from, {
        ...to,
        delay,
        onComplete: () => el.classList.add("gsap-animated"),
        scrollTrigger: {
          trigger: el,
          start: ST.start,
          toggleActions: ST.toggleActions,
        },
      });
      this.animations.set(el, tw);
    });
  }

  initHighlightText() {
    const nodes = document.querySelectorAll("[data-highlight-text]");
    if (typeof SplitText === "undefined") return;

    const reduceMotion =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    nodes.forEach((heading) => {
      if (
        heading.closest(".header-full-screen") ||
        heading.hasAttribute("data-no-animate")
      )
        return;
      if (!heading.textContent?.trim()) return;

      if (reduceMotion && !heading.hasAttribute("data-allow-motion")) return;
      if (heading.hasAttribute("data-no-animate")) return;

      try {
        const scrollStart =
          heading.getAttribute("data-highlight-scroll-start") || "top 90%";
        const scrollEnd =
          heading.getAttribute("data-highlight-scroll-end") || "center 40%";
        const fadedValue =
          parseFloat(heading.getAttribute("data-highlight-fade")) || 0.2;
        const staggerValue =
          parseFloat(heading.getAttribute("data-highlight-stagger")) || 0.1;

        new SplitText(heading, {
          type: "words, chars",
          autoSplit: true,
          // Returning the timeline lets SplitText revert it before re-splitting
          onSplit(self) {
            if (!self.chars?.length) return;
            const tl = gsap.timeline({
              scrollTrigger: {
                scrub: true,
                trigger: heading,
                start: scrollStart,
                end: scrollEnd,
              },
            });
            tl.from(self.chars, {
              autoAlpha: fadedValue,
              stagger: staggerValue,
              ease: "linear",
            });
            return tl;
          },
        });
      } catch {
        //
      }
    });
  }

  setupAnimations() {
    const hasTargets = document.querySelector(
      ".gsap-fade-in, .gsap-slide-left, .gsap-slide-right, .gsap-slide-up, .gsap-slide-down, .gsap-scale, .gsap-stagger, .gsap-stagger-slow, [data-gsap-animate], [data-highlight-text]",
    );
    if (!hasTargets) {
      return;
    }

    this._revealsFromClasses();
    document.querySelectorAll(".gsap-stagger").forEach((el) => {
      if (!skipReveal(el))
        this._staggerContainer(el, 1, 0.14, 0.25);
    });
    document.querySelectorAll(".gsap-stagger-slow").forEach((el) => {
      if (!skipReveal(el))
        this._staggerContainer(el, 1.2, 0.28, 0.35);
    });
    this._dataAttributeAnimations();
    this.initHighlightText();

    const smoothScroll = window.SOJ_SMOOTH_SCROLL;
    if (smoothScroll?.scheduleRefreshDimensions) {
      smoothScroll.scheduleRefreshDimensions();
    } else {
      ScrollTrigger.refresh();
    }
  }

  createCustomAnimation(
    element,
    fromProps,
    toProps,
    scrollTriggerOptions = {},
  ) {
    const tween = gsap.fromTo(element, fromProps, {
      ...toProps,
      scrollTrigger: {
        trigger: element,
        start: ST.start,
        toggleActions: "play none none reverse",
        ...scrollTriggerOptions,
      },
    });
    this.animations.set(element, tween);
    return tween;
  }

  refresh() {
    ScrollTrigger.refresh();
  }

  killAll() {
    this.animations.forEach((anim) => {
      if (anim && typeof anim.kill === "function") anim.kill();
    });
    this.animations.clear();

    if (typeof ScrollTrigger.getAll === "function") {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    }
    gsap.killTweensOf("*");
  }

  getAnimation(element) {
    return this.animations.get(element);
  }

  getDelayFromClasses(element) {
    return delayFromClasses(element);
  }
}

export default GSAPAnimationSystem;
